/**
 * Settings panels for entry controls: each machine's tolerance and material
 * lists, the logins and the machines each one runs, the loom operators named
 * in the loom floor's dropdown, and the negative-stock policy.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, request } from "../../lib/api";
import { downloadFile } from "../../lib/api2";
import {
  controls,
  uploadMachineMaterials,
  type AdminMachine,
  type AdminUser,
  type ContextItem,
  type Direction,
  type MaterialImport,
} from "../../lib/controls";

const DIRECTIONS: [Direction, string][] = [
  ["INPUT", "Input"],
  ["OUTPUT", "Output"],
  ["WASTE", "Waste"],
];

type Tone = { tone: "positive" | "negative" | "warning"; text: string } | null;

function Banner({ value, onClose }: { value: Tone; onClose: () => void }) {
  if (!value) return null;
  return (
    <div className={`banner banner--${value.tone}`} role="status">
      <div>{value.text}</div>
      <button className="btn btn--ghost btn--auto" onClick={onClose}>Dismiss</button>
    </div>
  );
}

// -- Machines ------------------------------------------------------------------

/** The unit a machine's capacity is counted in. */
function unitOf(machine: AdminMachine): string {
  return (machine.capacity_uom ?? "KG").toLowerCase();
}

export function MachinesPanel() {
  const [machines, setMachines] = useState<AdminMachine[] | null>(null);
  const [items, setItems] = useState<ContextItem[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [banner, setBanner] = useState<Tone>(null);
  const [preview, setPreview] = useState<{ file: File; result: MaterialImport } | null>(null);

  const load = useCallback(() => {
    controls.adminMachines().then(setMachines)
      .catch((e: ApiError) => setBanner({ tone: "negative", text: e.message }));
    controls.context().then((c) => setItems(c.items)).catch(() => undefined);
  }, []);
  useEffect(load, [load]);

  async function saveTolerance(machine: AdminMachine, value: string) {
    const pct = Number(value);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      setBanner({ tone: "warning", text: "Tolerance is a percentage between 0 and 100." });
      return;
    }
    try {
      const saved = await controls.saveMachine(machine.code, { tolerance_pct: pct });
      setMachines((list) => list?.map((m) => (m.code === saved.code ? saved : m)) ?? null);
      setBanner({ tone: "positive", text: `${machine.name}: tolerance is now ${pct}%.` });
    } catch (e) {
      setBanner({ tone: "negative", text: (e as ApiError).message });
    }
  }

  async function saveCapacity(machine: AdminMachine, value: string) {
    const capacity = value.trim() === "" ? 0 : Number(value);
    if (!Number.isFinite(capacity) || capacity < 0) {
      setBanner({ tone: "warning", text: "Capacity per day is a quantity of 0 or more. Leave it empty to clear it." });
      return;
    }
    try {
      const saved = await controls.saveMachine(machine.code, { capacity_per_day: capacity });
      setMachines((list) => list?.map((m) => (m.code === saved.code ? saved : m)) ?? null);
      setBanner({
        tone: "positive",
        text: capacity
          ? `${machine.name}: capacity is now ${capacity.toLocaleString("en-IN")} ${unitOf(saved)} a day.`
          : `${machine.name}: capacity cleared.`,
      });
    } catch (e) {
      setBanner({ tone: "negative", text: (e as ApiError).message });
    }
  }

  async function saveMaterials(machine: AdminMachine, direction: Direction, codes: string[]) {
    try {
      const saved = await controls.saveMachine(machine.code, { materials: { [direction]: codes } });
      setMachines((list) => list?.map((m) => (m.code === saved.code ? saved : m)) ?? null);
    } catch (e) {
      setBanner({ tone: "negative", text: (e as ApiError).message });
    }
  }

  async function chooseFile(file: File | undefined) {
    if (!file) return;
    try {
      const result = await uploadMachineMaterials(file, "preview");
      setPreview({ file, result });
    } catch (e) {
      setBanner({ tone: "negative", text: (e as ApiError).message });
    }
  }

  async function commit() {
    if (!preview) return;
    try {
      const result = await uploadMachineMaterials(preview.file, "commit");
      if (result.saved) {
        setBanner({
          tone: "positive",
          text: `Material lists saved for ${Object.keys(result.machines).length} machine(s).`,
        });
        setPreview(null);
        load();
      } else {
        setPreview({ file: preview.file, result });
      }
    } catch (e) {
      setBanner({ tone: "negative", text: (e as ApiError).message });
    }
  }

  return (
    <>
      <Banner value={banner} onClose={() => setBanner(null)} />
      <section className="card">
        <h3 className="section-title">Material lists from Excel</h3>
        <p className="section-note">
          Download the current lists, fill in one row per material — Machine, Direction (Input,
          Output or Waste), Material code — and upload. A machine in the file gets exactly the
          file's list; machines not in the file are left alone. One bad row and nothing is saved.
        </p>
        <div className="page__footer">
          <button className="btn btn--ghost btn--auto"
                  onClick={() => downloadFile("/api/admin/machine-materials/export",
                                              "machine-materials.xlsx")
                    .catch((e: ApiError) => setBanner({ tone: "negative", text: e.message }))}>
            Download current lists
          </button>
          <label className="inputgroup">
            <span className="label">Upload a filled sheet</span>
            <input type="file" accept=".xlsx,.csv"
                   onChange={(e) => {
                     chooseFile(e.target.files?.[0]);
                     e.target.value = "";
                   }} />
          </label>
        </div>
        {preview && (
          <div className={`banner banner--${preview.result.errors.length ? "negative" : "warning"}`}>
            <div>
              <strong>{preview.file.name}: {preview.result.rows} rows.</strong>
              {preview.result.errors.length ? (
                <ul className="banner__list">
                  {preview.result.errors.slice(0, 12).map((e) => <li key={e}>{e}</li>)}
                  {preview.result.errors.length > 12 && (
                    <li>…and {preview.result.errors.length - 12} more.</li>
                  )}
                </ul>
              ) : (
                <ul className="banner__list">
                  {Object.entries(preview.result.machines).map(([code, counts]) => (
                    <li key={code}>
                      {code}: {counts.INPUT} input, {counts.OUTPUT} output, {counts.WASTE} waste
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="drill__actions">
              {!preview.result.errors.length && (
                <button className="btn btn--auto" onClick={commit}>Save these lists</button>
              )}
              <button className="btn btn--ghost btn--auto" onClick={() => setPreview(null)}>Cancel</button>
            </div>
          </div>
        )}
      </section>

      <section className="card">
        <h3 className="section-title">Machines</h3>
        <p className="section-note">
          Tolerance is the unaccounted percentage a shift may carry before it needs a reason and
          the unit head's approval. Capacity per day is what the machine can make in a day on
          both shifts; Machine efficiency measures the tape plant and RP machines against it.
          A machine with no list for a direction offers every material.
        </p>
        {!machines ? (
          <div className="skeleton state__bar" />
        ) : (
          <div className="tablewrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Machine</th>
                  <th className="num">Tolerance %</th>
                  <th className="num">Capacity / day</th>
                  {DIRECTIONS.map(([, label]) => <th key={label}>{label} materials</th>)}
                  <th aria-label="Edit" />
                </tr>
              </thead>
              <tbody>
                {machines.map((m) => (
                  <MachineRow key={m.code} machine={m} items={items}
                              open={open === m.code}
                              onToggle={() => setOpen(open === m.code ? null : m.code)}
                              onTolerance={(v) => saveTolerance(m, v)}
                              onCapacity={(v) => saveCapacity(m, v)}
                              onMaterials={(d, codes) => saveMaterials(m, d, codes)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

function MachineRow({
  machine,
  items,
  open,
  onToggle,
  onTolerance,
  onCapacity,
  onMaterials,
}: {
  machine: AdminMachine;
  items: ContextItem[];
  open: boolean;
  onToggle: () => void;
  onTolerance: (value: string) => void;
  onCapacity: (value: string) => void;
  onMaterials: (direction: Direction, codes: string[]) => void;
}) {
  const current = Number((Number(machine.tolerance_pct) * 100).toFixed(2)).toString();
  const [tolerance, setTolerance] = useState(current);
  useEffect(() => setTolerance(current), [current]);
  const savedCapacity = machine.capacity_per_day === null
    ? ""
    : Number(machine.capacity_per_day).toString();
  const [capacity, setCapacity] = useState(savedCapacity);
  useEffect(() => setCapacity(savedCapacity), [savedCapacity]);

  return (
    <>
      <tr>
        <td>{machine.name} <span className="cell__unit">{machine.code}</span></td>
        <td className="num">
          <input className="num mono tol" inputMode="decimal" value={tolerance}
                 aria-label={`${machine.name} tolerance percent`}
                 onChange={(e) => setTolerance(e.target.value)}
                 onBlur={() => tolerance !== current && onTolerance(tolerance)}
                 onKeyDown={(e) => e.key === "Enter" && tolerance !== current && onTolerance(tolerance)} />
        </td>
        <td className="num">
          <input className="num mono tol" inputMode="decimal" value={capacity} placeholder="—"
                 aria-label={`${machine.name} capacity per day`}
                 onChange={(e) => setCapacity(e.target.value)}
                 onBlur={() => capacity !== savedCapacity && onCapacity(capacity)}
                 onKeyDown={(e) => e.key === "Enter" && capacity !== savedCapacity && onCapacity(capacity)} />
          <span className="cell__unit"> {unitOf(machine)}</span>
        </td>
        {DIRECTIONS.map(([d]) => (
          <td key={d}>
            {machine.materials[d].length
              ? `${machine.materials[d].length} · ${machine.materials[d].slice(0, 3).join(", ")}${machine.materials[d].length > 3 ? "…" : ""}`
              : <span className="cell__unit">any</span>}
          </td>
        ))}
        <td>
          <button className="btn btn--ghost btn--auto btn--sm" onClick={onToggle}>
            {open ? "Done" : "Materials"}
          </button>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7}>
            <div className="twocol">
              {DIRECTIONS.map(([d, label]) => (
                <MaterialPicker key={d} label={label} items={items}
                                chosen={machine.materials[d]}
                                onChange={(codes) => onMaterials(d, codes)} />
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function MaterialPicker({
  label,
  items,
  chosen,
  onChange,
}: {
  label: string;
  items: ContextItem[];
  chosen: string[];
  onChange: (codes: string[]) => void;
}) {
  const [query, setQuery] = useState("");
  const set = new Set(chosen);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((i) => !q || i.code.toLowerCase().includes(q) || i.name.toLowerCase().includes(q));
  }, [items, query]);

  return (
    <div>
      <span className="label">{label} — {chosen.length ? `${chosen.length} chosen` : "any material"}</span>
      <div className="chipset" style={{ margin: "var(--sp-2) 0" }}>
        {chosen.map((code) => (
          <span key={code} className="chip chip--on">
            {code}
            <button aria-label={`Remove ${code}`} onClick={() => onChange(chosen.filter((c) => c !== code))}>×</button>
          </span>
        ))}
      </div>
      <input placeholder="Find a material" value={query} onChange={(e) => setQuery(e.target.value)} />
      <div className="chipset" style={{ marginTop: "var(--sp-2)", maxHeight: 160, overflowY: "auto" }}>
        {shown.filter((i) => !set.has(i.code)).slice(0, 60).map((i) => (
          <button key={i.code} type="button" className="chip" title={i.name}
                  onClick={() => onChange([...chosen, i.code])}>
            + {i.code}
          </button>
        ))}
      </div>
    </div>
  );
}

// -- Users -------------------------------------------------------------------

const ROLES = ["OPERATOR", "SUPERVISOR", "CLERK", "STORE_KEEPER", "WEIGHBRIDGE",
               "ACCOUNTS", "COSTING", "UNIT_HEAD", "AUDITOR", "SUPER_ADMIN"];

export function UsersPanel({ selfId }: { selfId: number }) {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [machines, setMachines] = useState<AdminMachine[]>([]);
  const [banner, setBanner] = useState<Tone>(null);
  const [draft, setDraft] = useState({ email: "", name: "", role: "OPERATOR", password: "", machines: [] as string[] });
  const [editing, setEditing] = useState<number | null>(null);

  const load = useCallback(() => {
    controls.users().then(setUsers).catch((e: ApiError) => setBanner({ tone: "negative", text: e.message }));
    controls.adminMachines().then(setMachines).catch(() => undefined);
  }, []);
  useEffect(load, [load]);

  async function create() {
    try {
      const user = await controls.createUser(draft);
      setBanner({ tone: "positive", text: `${user.name} can now sign in as ${user.email}. Give them the password you set.` });
      setDraft({ email: "", name: "", role: "OPERATOR", password: "", machines: [] });
      load();
    } catch (e) {
      setBanner({ tone: "negative", text: (e as ApiError).message });
    }
  }

  async function update(user: AdminUser, body: Parameters<typeof controls.updateUser>[1], note: string) {
    try {
      await controls.updateUser(user.id, body);
      setBanner({ tone: "positive", text: note });
      load();
    } catch (e) {
      setBanner({ tone: "negative", text: (e as ApiError).message });
    }
  }

  const toggle = (list: string[], code: string) =>
    list.includes(code) ? list.filter((c) => c !== code) : [...list, code];

  return (
    <>
      <Banner value={banner} onClose={() => setBanner(null)} />
      <section className="card">
        <h3 className="section-title">Add a login</h3>
        <p className="section-note">
          An operator sees only Shift entry, Loom floor and Entries, for the machines ticked here.
          A supervisor or clerk with no machines ticked sees every machine.
        </p>
        <div className="formgrid">
          <label className="inputgroup"><span className="label">Name</span>
            <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></label>
          <label className="inputgroup"><span className="label">Email (the login)</span>
            <input value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} /></label>
          <label className="inputgroup"><span className="label">Role</span>
            <select value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })}>
              {ROLES.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ").toLowerCase()}</option>)}
            </select></label>
          <label className="inputgroup"><span className="label">Password (10+ characters)</span>
            <input type="password" autoComplete="new-password" value={draft.password}
                   onChange={(e) => setDraft({ ...draft, password: e.target.value })} /></label>
        </div>
        <span className="label">Machines</span>
        <div className="chipset" style={{ margin: "var(--sp-2) 0" }}>
          {machines.map((m) => (
            <button key={m.code} type="button"
                    className={`chip${draft.machines.includes(m.code) ? " chip--on" : ""}`}
                    aria-pressed={draft.machines.includes(m.code)}
                    onClick={() => setDraft({ ...draft, machines: toggle(draft.machines, m.code) })}>
              {draft.machines.includes(m.code) ? "✓ " : ""}{m.name}
            </button>
          ))}
        </div>
        <div className="page__footer">
          <button className="btn btn--auto" onClick={create}
                  disabled={!draft.name.trim() || !draft.email.trim() || draft.password.length < 10}>
            Add login
          </button>
        </div>
      </section>

      <section className="card">
        <h3 className="section-title">Logins</h3>
        {!users ? <div className="skeleton state__bar" /> : (
          <div className="tablewrap">
            <table className="table">
              <thead>
                <tr><th>Name</th><th>Email</th><th>Role</th><th>Machines</th><th>Status</th><th aria-label="Actions" /></tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <UserRow key={u.id} user={u} machines={machines} self={u.id === selfId}
                           open={editing === u.id}
                           onToggle={() => setEditing(editing === u.id ? null : u.id)}
                           onSave={(body, note) => update(u, body, note)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

function UserRow({
  user,
  machines,
  self,
  open,
  onToggle,
  onSave,
}: {
  user: AdminUser;
  machines: AdminMachine[];
  self: boolean;
  open: boolean;
  onToggle: () => void;
  onSave: (body: Parameters<typeof controls.updateUser>[1], note: string) => void;
}) {
  const [chosen, setChosen] = useState(user.machines);
  const [role, setRole] = useState(user.role);
  const [password, setPassword] = useState("");
  useEffect(() => {
    setChosen(user.machines);
    setRole(user.role);
  }, [user]);

  return (
    <>
      <tr className={user.active ? undefined : "row--deleted"}>
        <td>{user.name}{self ? " (you)" : ""}</td>
        <td>{user.email}</td>
        <td>{user.role.replace(/_/g, " ").toLowerCase()}</td>
        <td>{user.machines.length ? user.machines.join(", ") : <span className="cell__unit">{user.role === "OPERATOR" ? "none" : "all"}</span>}</td>
        <td>{user.active ? "active" : "deactivated"}</td>
        <td><button className="btn btn--ghost btn--auto btn--sm" onClick={onToggle}>{open ? "Close" : "Change"}</button></td>
      </tr>
      {open && (
        <tr>
          <td colSpan={6}>
            <div className="formgrid">
              <label className="inputgroup"><span className="label">Role</span>
                <select value={role} onChange={(e) => setRole(e.target.value)} disabled={self}>
                  {ROLES.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ").toLowerCase()}</option>)}
                </select></label>
              <label className="inputgroup"><span className="label">New password (leave blank to keep)</span>
                <input type="password" autoComplete="new-password" value={password}
                       onChange={(e) => setPassword(e.target.value)} /></label>
            </div>
            <span className="label">Machines</span>
            <div className="chipset" style={{ margin: "var(--sp-2) 0" }}>
              {machines.map((m) => (
                <button key={m.code} type="button" className={`chip${chosen.includes(m.code) ? " chip--on" : ""}`}
                        aria-pressed={chosen.includes(m.code)}
                        onClick={() => setChosen(chosen.includes(m.code) ? chosen.filter((c) => c !== m.code) : [...chosen, m.code])}>
                  {chosen.includes(m.code) ? "✓ " : ""}{m.name}
                </button>
              ))}
            </div>
            <div className="page__footer">
              <button className="btn btn--auto" disabled={password.length > 0 && password.length < 10}
                      onClick={() => onSave({ role, machines: chosen, ...(password ? { password } : {}) },
                                            `${user.name} updated.`)}>
                Save
              </button>
              {!self && (
                <button className="btn btn--ghost btn--auto"
                        onClick={() => onSave({ active: !user.active },
                                              `${user.name} ${user.active ? "can no longer sign in" : "can sign in again"}.`)}>
                  {user.active ? "Deactivate" : "Reactivate"}
                </button>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// -- Loom operators ------------------------------------------------------------

interface OperatorRow { id: number; code: string; name: string; active: boolean }

export function OperatorsPanel() {
  const [rows, setRows] = useState<OperatorRow[] | null>(null);
  const [name, setName] = useState("");
  const [banner, setBanner] = useState<Tone>(null);

  const load = useCallback(() => {
    request<OperatorRow[]>("/api/admin/operators").then(setRows)
      .catch((e: ApiError) => setBanner({ tone: "negative", text: e.message }));
  }, []);
  useEffect(load, [load]);

  async function add() {
    try {
      setRows(await request<OperatorRow[]>("/api/admin/operators", { method: "POST", body: { name } }));
      setName("");
    } catch (e) {
      setBanner({ tone: "negative", text: (e as ApiError).message });
    }
  }

  async function toggle(row: OperatorRow) {
    try {
      setRows(await request<OperatorRow[]>(`/api/admin/operators/${row.id}`, {
        method: "PUT", body: { name: row.name, active: !row.active },
      }));
    } catch (e) {
      setBanner({ tone: "negative", text: (e as ApiError).message });
    }
  }

  return (
    <>
      <Banner value={banner} onClose={() => setBanner(null)} />
      <section className="card">
        <h3 className="section-title">Loom operators</h3>
        <p className="section-note">
          The names in the loom floor's Operator dropdown. They do not sign in — whoever enters the
          floor picks them. Deactivating one removes them from the dropdown and keeps their history.
        </p>
        <div className="page__footer">
          <label className="inputgroup inputgroup--grow"><span className="label">Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && name.trim().length > 1 && add()} /></label>
          <button className="btn btn--auto" onClick={add} disabled={name.trim().length < 2}>Add operator</button>
        </div>
        {!rows ? <div className="skeleton state__bar" /> : (
          <div className="chipset">
            {rows.map((r) => (
              <span key={r.id} className={`chip${r.active ? " chip--on" : ""}`}>
                {r.name} · {r.code}
                <button onClick={() => toggle(r)} aria-label={r.active ? `Deactivate ${r.name}` : `Reactivate ${r.name}`}>
                  {r.active ? "×" : "↺"}
                </button>
              </span>
            ))}
            {rows.length === 0 && <span className="page__note">No operators yet.</span>}
          </div>
        )}
      </section>
    </>
  );
}

// -- Stock policy --------------------------------------------------------------

export function StockPolicyPanel() {
  const [policy, setPolicy] = useState<"WARN" | "BLOCK" | null>(null);
  const [banner, setBanner] = useState<Tone>(null);

  useEffect(() => {
    controls.settings().then((s) => setPolicy(s.negative_stock_policy))
      .catch((e: ApiError) => setBanner({ tone: "negative", text: e.message }));
  }, []);

  async function save(value: "WARN" | "BLOCK") {
    try {
      const s = await controls.saveSettings({ negative_stock_policy: value });
      setPolicy(s.negative_stock_policy);
      setBanner({ tone: "positive", text: value === "WARN"
        ? "Negative stock is allowed. Each entry that causes it warns the person and goes on the Exceptions report."
        : "Negative stock is refused. An entry that would cause it is not saved." });
    } catch (e) {
      setBanner({ tone: "negative", text: (e as ApiError).message });
    }
  }

  return (
    <>
      <Banner value={banner} onClose={() => setBanner(null)} />
      <section className="card">
        <h3 className="section-title">Negative stock</h3>
        <p className="section-note">
          Applies everywhere stock moves: shift entry, the loom floor, printing, cutting, stitching,
          dispatch and transfers.
        </p>
        {!policy ? <div className="skeleton state__bar" /> : (
          <div className="segmented" role="radiogroup" aria-label="Negative stock">
            <button className={policy === "WARN" ? "is-active" : ""} role="radio"
                    aria-checked={policy === "WARN"} onClick={() => save("WARN")}>
              Allow, with a warning
            </button>
            <button className={policy === "BLOCK" ? "is-active" : ""} role="radio"
                    aria-checked={policy === "BLOCK"} onClick={() => save("BLOCK")}>
              Refuse
            </button>
          </div>
        )}
      </section>
    </>
  );
}
